import { computed, type MaybeRef, unref } from "vue";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import type { AdminWalletTransaction, AdminWalletTransactionsQueryParams } from "@shopexperts/legacy-api";

interface UseAdminOrganizationWalletTransactionsOptions {
  organizationId: MaybeRef<number | null | undefined>;
  filters?: MaybeRef<AdminWalletTransactionsQueryParams | undefined>;
  perPage?: number;
  enabled?: MaybeRef<boolean>;
}

function isPositiveId(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function omitOrganizationId(filters: AdminWalletTransactionsQueryParams): AdminWalletTransactionsQueryParams {
  const { organization_id: _organizationId, ...rest } = filters;
  return rest;
}

export function useAdminOrganizationWalletTransactions(options: UseAdminOrganizationWalletTransactionsOptions) {
  const api = useApi();
  const perPage = options.perPage ?? 20;
  const organizationId = computed(() => unref(options.organizationId));
  const enabled = computed(() => (unref(options.enabled) ?? true) && isPositiveId(organizationId.value));
  const normalizedFilters = computed<AdminWalletTransactionsQueryParams>(() => ({
    ...omitOrganizationId(unref(options.filters) ?? {}),
    per_page: perPage,
  }));

  const query = useInfiniteQuery({
    queryKey: computed(() => ["admin", "wallet", "organization", organizationId.value, "transactions", normalizedFilters.value]),
    queryFn: async ({ pageParam }) => {
      if (!isPositiveId(organizationId.value)) {
        throw new Error("organizationId is required to load organization wallet transactions");
      }

      return await api.admin.wallet.listOrganizationTransactions({
        params: { organization: organizationId.value },
        query: {
          ...normalizedFilters.value,
          cursor: pageParam,
        },
      });
    },
    getNextPageParam: (lastPage) => lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined,
    initialPageParam: undefined as string | undefined,
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  const transactions = computed<AdminWalletTransaction[]>(() =>
    query.data.value?.pages.flatMap((page) => page.data) ?? [],
  );

  return {
    transactions,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    query,
  };
}
